import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import {
  MessageSquare,
  Code,
  Eye,
  Settings,
  Container,
  FileText,
  ArrowRight,
  Sparkles,
  DollarSign
} from "lucide-react";
import { Link } from "react-router-dom";

export const FeaturesSection = () => {
  const features = [
    {
      icon: Sparkles,
      title: "Model & Persona Wizard",
      description: "Interactive wizard to design your AI infrastructure step-by-step",
      href: "/wizard",
      color: "bg-violet-500",
      features: ["Environment setup", "Persona creation", "Model selection", "Smart recommendations"]
    },
    {
      icon: MessageSquare,
      title: "Demo Center",
      description: "Experience live AI models with real-time streaming responses",
      href: "/demos",
      color: "bg-blue-500",
      features: ["Chat & Q&A", "Code Generation", "Visual Analysis", "Custom Prompts"]
    },
    {
      icon: Settings,
      title: "Model Selector",
      description: "Find the perfect model for your industry and use case",
      href: "/models",
      color: "bg-green-500",
      features: ["Industry-specific", "Use case matching", "Performance metrics", "Licensing info"]
    },
    {
      icon: Sparkles,
      title: "Infrastructure Sizing",
      description: "Calculate optimal hardware requirements for your workload",
      href: "/sizing",
      color: "bg-purple-500",
      features: ["Resource calculator", "Dell server configs", "Cost estimation", "Scalability planning"]
    },
    {
      icon: Container,
      title: "Container Catalog",
      description: "Ready-to-deploy inference microservices and images",
      href: "/containers",
      color: "bg-orange-500",
      features: ["Pre-built images", "Kubernetes manifests", "Docker compose", "Helm charts"]
    },
    {
      icon: Code,
      title: "Custom Installer",
      description: "Generate tailored deployment packages for your environment",
      href: "/installer",
      color: "bg-cyan-500",
      features: ["Custom configs", "Security settings", "OS compatibility", "One-click deploy"]
    },
    {
      icon: FileText,
      title: "Collateral Library",
      description: "Access white papers, case studies, and technical resources",
      href: "/collateral",
      color: "bg-pink-500",
      features: ["Technical docs", "Case studies", "ROI calculators", "Best practices"]
    },
    {
      icon: DollarSign,
      title: "Cost Simulator",
      description: "Compare on-premises vs cloud costs with detailed capacity planning",
      href: "/cost-simulator",
      color: "bg-emerald-500",
      features: ["OpEx vs CapEx analysis", "Workload simulation", "Break-even analysis", "Export reports"]
    }
  ];

  return (
    <section className="py-24 bg-muted/30">
      <div className="container mx-auto px-4">
        <div className="max-w-3xl mx-auto text-center mb-16 space-y-4">
          <h2 className="text-3xl md:text-4xl font-bold">
            Everything You Need for{" "}
            <span className="bg-gradient-primary bg-clip-text text-transparent">
              GenAI Success
            </span>
          </h2>
          <p className="text-xl text-muted-foreground leading-relaxed">
            From live demos to production deployment, we provide the complete toolkit 
            for enterprise GenAI implementation.
          </p>
        </div>

        <div data-tour="features" className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-7xl mx-auto">
          {features.map((feature, index) => (
            <Card 
              key={feature.title}
              className="group hover:shadow-elegant transition-smooth bg-gradient-card border-0 animate-fade-up"
              style={{ animationDelay: `${index * 100}ms` }}
            >
              <CardHeader className="space-y-4">
                <div className="flex items-center space-x-4">
                  <div className={`p-3 rounded-xl ${feature.color} bg-opacity-10`}>
                    <feature.icon className="h-6 w-6 text-primary" />
                  </div>
                  <div>
                    <CardTitle className="text-xl group-hover:text-primary transition-smooth">
                      {feature.title}
                    </CardTitle>
                  </div>
                </div>
                <CardDescription className="text-base leading-relaxed">
                  {feature.description}
                </CardDescription>
              </CardHeader>
              
              <CardContent className="space-y-6">
                <ul className="space-y-2">
                  {feature.features.map((item) => (
                    <li key={item} className="flex items-center text-sm text-muted-foreground">
                      <div className="w-1.5 h-1.5 bg-primary rounded-full mr-3 flex-shrink-0" />
                      {item}
                    </li>
                  ))}
                </ul>
                
                <Button
                  asChild
                  variant="ghost"
                  className="w-full group-hover:bg-primary group-hover:text-primary-foreground transition-smooth"
                >
                  <Link to={feature.href}>
                    Explore {feature.title}
                    <ArrowRight className="ml-2 h-4 w-4 group-hover:translate-x-1 transition-smooth" />
                  </Link>
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Bottom CTA */}
        <div className="text-center mt-16">
          <Button
            asChild
            size="lg"
            className="bg-gradient-primary hover:shadow-glow transition-smooth text-lg px-8 py-4 h-auto"
          >
            <Link to="/demos">
              Start Your GenAI Journey
              <ArrowRight className="ml-2 h-5 w-5" />
            </Link>
          </Button>
        </div>
      </div>
    </section>
  );
};